/**
 * SJSU brand colors.
 *
 * Source: SJSU University Marketing & Communications brand guide
 * (https://www.sjsu.edu/communications/brand/visual-style/color/web.php).
 *
 *   Primary Blue: #0055A2 (Pantone 300C)
 *   Primary Gold: #E5A823 (Pantone 124C)
 *
 * The 50–900 shade ladders are HSL-based interpolations around the
 * brand color (set as the `700` shade and as DEFAULT). SJSU has not
 * published an extended-palette spec; these are derived for UI use,
 * brand-aligned but not officially endorsed.
 *
 * Logo policy mirrors UMD's: text-only branding (university name in
 * brand colors), no SJSU wordmark or Spartan-head asset embedded in
 * the app. Avoids the third-party-logo-license question entirely.
 */
import type { ThemeColors } from '../types';

export const sjsuColors: ThemeColors = {
  // Primary — SJSU Blue (Pantone 300C, #0055A2 = rgb(0, 85, 162))
  '--color-primary':     '0 85 162',
  // -strong: SJSU blue already passes white text (7.44:1) — same as base.
  '--color-primary-strong': '0 85 162',
  '--color-primary-50':  '240 248 255',
  '--color-primary-100': '214 236 255',
  '--color-primary-200': '173 216 255',
  '--color-primary-300': '112 187 255',
  '--color-primary-400': '41 153 255',
  '--color-primary-500': '0 123 235',
  '--color-primary-600': '0 102 194',
  '--color-primary-700': '0 85 162',
  '--color-primary-800': '0 64 122',
  '--color-primary-900': '0 43 82',

  // Accent — SJSU Gold (Pantone 124C, #E5A823 = rgb(229, 168, 35))
  '--color-accent':     '229 168 35',
  '--color-accent-50':  '253 250 241',
  '--color-accent-100': '251 241 219',
  '--color-accent-200': '246 226 182',
  '--color-accent-300': '240 204 127',
  '--color-accent-400': '232 179 63',
  '--color-accent-500': '210 152 25',
  '--color-accent-600': '173 125 20',
  '--color-accent-700': '229 168 35',
  '--color-accent-800': '109 79 13',
  '--color-accent-900': '73 53 9',

  // Brand-mark faces (components/ui/BrandMark.tsx). SJSU Blue sits at L≈32%,
  // darker than UMD Red, so the ramp below it is tighter (~10% steps rather
  // than ~14%). Still enough separation to read the three faces, and it keeps
  // the brand color on the front face where the mark's identity lives.
  '--color-mark-front': '0 85 162',
  '--color-mark-top':   '0 58 112',
  '--color-mark-side':  '0 34 66',
  '--color-mark-front-dm': '77 163 255',
  '--color-mark-top-dm':   '26 118 214',
  '--color-mark-side-dm':  '0 78 148',
};
